import React, { useRef, useState, useCallback } from 'react'
import { BaseModalV2 } from 'components/ui/BaseModal'
import { Box, Flex, Text, Button, Avatar, useToast, Input, Image } from '@chakra-ui/core'
import { Image as ImageIcon, Smartphone } from 'lucide-react'
import LocaleText from './LocaleText'
import { useFileValidation } from 'hooks/useFileValidation'
import useUploadService from 'hooks/useUploadService'
import { getFileExtension } from 'utils'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import imagePlaceholderAnimated from 'assets/placeholder-640.svg'
import strings from 'constants/strings'
import { AppDispatch } from 'config/redux'
import { updateUser } from 'redux/actions/admin'
import { useCamera } from 'hooks/useCamera'

interface UploadAvatarPopUpProps {
  isOpen: boolean
  onClose: () => void
  onUploadFromGallery: () => void
  onTakeSelfie: () => void
  onRemindLater?: () => void
}

const UploadAvatarHeader: React.FC = () => {
  return (
    <Flex w="100%" justifyContent="center">
      <Text fontSize={{ base: '22px', md: '28px' }} fontWeight="800">
        <LocaleText text="on_boarding_header" />
      </Text>
    </Flex>
  )
}

interface BodyProps {
  onClose: () => void
}

interface CameraCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (file: File) => void
  isBusy?: boolean
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = React.memo(({ isOpen, onClose, onConfirm, isBusy }) => {
  const {
    cameraIsLoading,
    imagePreview,
    imageFiles,
    videoRef,
    canvasRef,
    inputRef: cameraInputRef,
    takePhoto,
    handleChangeEvent: handleCameraInputChange,
    resetCamera,
    stopCamera,
  } = useCamera(isOpen)

  const handleCloseCamera = React.useCallback(() => {
    try {
      stopCamera()
    } catch (e) {
      // ignore stop errors
    }
    onClose()
  }, [stopCamera, onClose])

  const handleUsePhoto = () => {
    if (imageFiles.length > 0) {
      onConfirm(imageFiles[0])
    }
  }

  return (
    <BaseModalV2
      isOpen={isOpen}
      onClose={handleCloseCamera}
      header={
        <Flex w="100%" justifyContent="space-between" alignItems="center">
          <Text fontWeight="bold">
            <LocaleText text="on_boarding_open_camera_btn" />
          </Text>
          <Button variant="ghost" onClick={handleCloseCamera}>
            <LocaleText text="close" />
          </Button>
        </Flex>
      }
      body={
        <Flex w="100%" alignItems="center" justifyContent="center" flexDir="column" py="10px" bg="white">
          {imagePreview.length > 0 ? (
            <Image src={imagePreview[0]} maxW="100%" height="500px" borderRadius="10px" />
          ) : (
            <>
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '500px', borderRadius: 10 }} />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          )}
          <Input
            ref={cameraInputRef}
            accept={`${strings.SERVER_IMAGE_FORMATS}`}
            type="file"
            multiple={false}
            id="camera-image-input"
            style={{ display: 'none' }}
            onChange={handleCameraInputChange}
          />
        </Flex>
      }
      footer={
        <Flex w="100%" justifyContent="flex-end" gridGap="10px">
          {imagePreview.length > 0 ? (
            <>
              <Button onClick={handleUsePhoto} isLoading={isBusy} isDisabled={cameraIsLoading || isBusy}>
                <LocaleText text="use" />
              </Button>
              <Button variant="outline" onClick={resetCamera} isDisabled={!!isBusy}>
                <LocaleText text="retake" />
              </Button>
            </>
          ) : (
            <Button onClick={takePhoto} isDisabled={cameraIsLoading} isLoading={cameraIsLoading}>
              <LocaleText text="open_camera" />
            </Button>
          )}
        </Flex>
      }
      modalContentProps={{ w: '100%', maxW: { base: '90vw', md: '720px' }, minW: '350px' }}
      modalBodyProps={{ height: 'auto', maxH: '80dvh', bg: 'white' }}
      modalHeaderProps={{ boxShadow: 'none' }}
    />
  )
})

const UploadAvatarBody: React.FC<BodyProps> = React.memo(({ onClose }) => {
  const { myUID, retailUsersObject } = useSelector((state: RootState) => state.config.config)

  const userAvatar = myUID && retailUsersObject ? retailUsersObject?.[myUID]?.profile_img_url : null

  const { uploadFile } = useUploadService()
  const { fileSizeValidation } = useFileValidation()
  const toast = useToast()
  const dispatch: AppDispatch = useDispatch()
  const imageInputRef = useRef<HTMLInputElement>(null)
  const [uploadedAvatar, setUploadedAvatar] = useState<string>(userAvatar || '')
  const [isAvatarUploading, setIsAvatarUploading] = useState<boolean>(false)
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleLocalFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0]
      try {
        const isValid = fileSizeValidation(file)
        if (!isValid) return
        setSelectedFile(file)
        const localUrl = URL.createObjectURL(file)
        setUploadedAvatar(localUrl)
        // сбрасываем value, чтобы повторный выбор того же файла сработал
        e.target.value = ''
      } catch (error) {
        toast({ title: 'Something went wrong', status: 'error', duration: 5000, isClosable: true })
        setUploadedAvatar('')
        setSelectedFile(null)
      }
    }
  }
  const handleChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault() // Prevent menu close on input change
    e.stopPropagation()
    if (isAvatarUploading) return
    try {
      setIsAvatarUploading(true)
      await handleLocalFileSelection(e)
    } catch (error) {
      toast({
        title: 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      console.log(error)
    } finally {
      setIsAvatarUploading(false)
    }
  }

  const openFileChooser = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (isAvatarUploading) return
      imageInputRef.current?.click()
    },
    [isAvatarUploading]
  )

  const openCamera = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (isAvatarUploading) return
      setIsCameraOpen(true)
    },
    [isAvatarUploading]
  )

  const closeCamera = useCallback(() => {
    setIsCameraOpen(false)
  }, [])

  const normalizeCameraFile = useCallback((file: File) => {
    let ext = file.type && file.type.includes('/') ? file.type.split('/')[1] : 'png'
    if (ext === 'jpg') {
      ext = 'jpeg'
    }
    const nameWithExt = `${file.name}.${ext}`
    return new File([file], nameWithExt, { type: file.type || `image/${ext}` })
  }, [])

  const handleConfirmPhoto = useCallback(
    async (file: File) => {
      try {
        setIsAvatarUploading(true)
        const normalized = normalizeCameraFile(file)
        const isValid = fileSizeValidation(normalized)
        if (!isValid) return
        setSelectedFile(normalized)
        const localUrl = URL.createObjectURL(normalized)
        setUploadedAvatar(localUrl)
        closeCamera()
      } catch (error) {
        toast({ title: 'Something went wrong', status: 'error', duration: 5000, isClosable: true })
        console.log(error)
      } finally {
        setIsAvatarUploading(false)
      }
    },
    [normalizeCameraFile, fileSizeValidation, toast, closeCamera]
  )
  const handleResetAvatar = () => {
    if (uploadedAvatar && uploadedAvatar.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(uploadedAvatar)
      } catch (e) {
        // ignore
      }
    }
    setUploadedAvatar('')
    setSelectedFile(null)
  }

  const handleApplyAvatar = async () => {
    if (!selectedFile || isSaving) return
    try {
      setIsSaving(true)
      const extension = getFileExtension(selectedFile.name).toLowerCase()
      const isSelfAvatarChange = true
      const fileUrl = await uploadFile(selectedFile, extension, isSelfAvatarChange)
      const usersObjectToPayload = { uid: myUID, profile_img_url: fileUrl }
      // @ts-ignore
      const res = await dispatch(updateUser(usersObjectToPayload, true))
      if (res) {
        toast({ title: 'Avatar updated', status: 'success', duration: 3000, isClosable: true })
        onClose()
      }
    } catch (error) {
      toast({ title: 'Something went wrong', status: 'error', duration: 5000, isClosable: true })
      console.log(error)
    } finally {
      setIsSaving(false)
    }
  }
  return (
    <Flex w="100%" h="100%" alignItems="center" justifyContent="center" flexDir="column" py="20px">
      <Avatar src={isAvatarUploading ? imagePlaceholderAnimated : uploadedAvatar} w="180px" h="180px" mb="24px" />
      <Flex w="100%" maxW="520px" flexDir="column" style={{ rowGap: 14 }}>
        <Button
          onClick={uploadedAvatar ? handleResetAvatar : openFileChooser}
          bg={uploadedAvatar ? '#4F46E5' : '#22C55E'}
          color="white"
          borderRadius="9999px"
          height={{ base: '48px', md: '56px' }}
          fontSize={{ base: '14px', md: '16px' }}
          _hover={{ opacity: 0.8 }}
          _active={{ opacity: 0.8 }}
          _focus={{ boxShadow: 'none' }}
          css={{ gap: '8px' }}
          isDisabled={isAvatarUploading || isSaving}
          isLoading={isAvatarUploading || isSaving}
        >
          <LocaleText text={uploadedAvatar ? 'on_boarding_change_image_btn' : 'on_boarding_upload_image_btn'} />
          {!uploadedAvatar && <ImageIcon size={24} style={{ transform: 'rotate(15deg)' }} />}
        </Button>

        <Button
          onClick={uploadedAvatar ? handleApplyAvatar : openCamera}
          bg={uploadedAvatar ? '#22C55E' : '#4F46E5'}
          color="white"
          borderRadius="9999px"
          height={{ base: '48px', md: '56px' }}
          fontSize={{ base: '14px', md: '16px' }}
          _hover={{ opacity: 0.8 }}
          _active={{ opacity: 0.8 }}
          _focus={{ boxShadow: 'none' }}
          css={{ gap: '8px' }}
          isDisabled={isAvatarUploading || isSaving}
          isLoading={isAvatarUploading || isSaving}
        >
          <LocaleText text={uploadedAvatar ? 'been_done' : 'on_boarding_open_camera_btn'} />
          {!uploadedAvatar && <Smartphone size={24} style={{ transform: 'rotate(-15deg)' }} />}
        </Button>
      </Flex>

      <Box mt="26px" maxW="560px" px="10px">
        <Text mb="6px">
          <LocaleText text={uploadedAvatar ? 'on_boarding_image_added' : 'on_boarding_sub_header'} />
        </Text>
      </Box>
      <Button
        variant="link"
        mt="26px"
        color="#2563EB"
        onClick={onClose}
        _focus={{ boxShadow: 'none' }}
        isDisabled={isAvatarUploading || isSaving}
      >
        <LocaleText text="skip" />
      </Button>
      <Input
        ref={imageInputRef}
        accept={`${strings.SERVER_IMAGE_FORMATS}`}
        type="file"
        multiple={false}
        id="upload-image-button"
        style={{ display: 'none' }}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onChange={handleChangeAvatar}
        isDisabled={isAvatarUploading || isSaving}
      />
      {isCameraOpen && (
        <CameraCaptureModal
          isOpen={isCameraOpen}
          onClose={closeCamera}
          onConfirm={handleConfirmPhoto}
          isBusy={isAvatarUploading}
        />
      )}
    </Flex>
  )
})

const UploadAvatarPopUp: React.FC<UploadAvatarPopUpProps> = ({ isOpen, onClose }) => {
  return (
    <BaseModalV2
      isOpen={isOpen}
      onClose={onClose}
      header={<UploadAvatarHeader />}
      body={<UploadAvatarBody onClose={onClose} />}
      footer={<></>}
      modalContentProps={{ w: '100%', maxW: { base: '90vw', md: '520px' }, minW: '350px' }}
      modalBodyProps={{ height: 'auto', maxH: '80dvh', bg: 'white' }}
      modalHeaderProps={{ boxShadow: 'none' }}
      modalFooterProps={{ display: 'none' }}
    />
  )
}

export default UploadAvatarPopUp
